import React, { useState } from "react";
import { Link } from "react-router-dom";
import { buildProjectUrl } from "../../services/buildUrl.js";
import { T } from "../../app/tokens.js";
import { Edit2, FolderKanban, MessageSquare } from "lucide-react";
import { OkrProgressBar } from "./OkrProgressBar.jsx";
import { OkrStatusBadge } from "./OkrStatusBadge.jsx";

export function OkrInitiative({ initiative, canEdit = false, onEdit, onUpdates }) {
  const [hover, setHover] = useState(false);

  const {
    id,
    title,
    owner,
    status,
    progressPercent,
    projectId,
    projectName,
    projectOwner,
    projectStatus,
    projectPhase,
    percentOverride
  } = initiative;

  const isLinked = !!projectId;
  const latestUpdate = initiative.updates?.[0];
  const displayTitle = isLinked ? (projectName || title) : title;
  const displayOwner = isLinked ? (projectOwner || owner) : owner;
  const displayStatus = isLinked ? (projectStatus || status) : status;

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex", alignItems: "center", gap: 16,
        padding: "8px 16px 8px 48px",
        background: hover ? T.hoverBg : "transparent",
        borderBottom: `1px solid ${T.border}`,
        transition: "background 0.2s ease"
      }}
    >
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 5 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ fontSize: T.fsSm, fontWeight: T.fwRegular, color: T.dark, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {displayTitle}
          {isLinked && title && title !== displayTitle && (
            <span style={{ marginLeft: 6, color: T.mid, fontSize: T.fsXxs }}>
              Initiative: {title}
            </span>
          )}
        </div>
        {isLinked && (
          <Link
            to={buildProjectUrl(projectId)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "2px 6px", borderRadius: 4, background: T.tile,
              color: T.mid, fontSize: T.fsXxs, textDecoration: "none"
            }}
            title={`Linked to project: ${projectName}`}
          >
            <FolderKanban size={12} />
            <span style={{ maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{projectName}</span>
          </Link>
        )}
        {isLinked && (
          <span style={{ fontSize: T.fsXxs, color: T.mid, whiteSpace: "nowrap" }}>
            {projectPhase ? projectPhase.replaceAll("_", " ") : "phase unknown"}
            {projectStatus ? ` · ${projectStatus.replaceAll("_", " ")}` : ""}
            {projectOwner ? ` · ${projectOwner}` : ""}
          </span>
        )}
        </div>
        <div style={{ fontSize: T.fsXs, color: T.mid, whiteSpace: "pre-wrap" }}>
          {latestUpdate ? `${latestUpdate.month} · ${latestUpdate.text}` : "No monthly updates yet"}
          {latestUpdate?.status && <span style={{ marginLeft: 8 }}><OkrStatusBadge status={latestUpdate.status} /></span>}
          {latestUpdate?.percent != null && <span style={{ marginLeft: 6 }}>{latestUpdate.percent}% snapshot</span>}
        </div>
      </div>

      <div style={{ width: 120, flexShrink: 0 }}>
        <div style={{ fontSize: T.fsXs, color: T.mid, marginBottom: 2 }}>Owner</div>
        <div style={{ fontSize: T.fsSm, color: T.dark, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{displayOwner || "—"}</div>
      </div>

      <div style={{ width: 100, flexShrink: 0 }}>
        <OkrStatusBadge status={displayStatus} />
      </div>

      <div style={{ width: 120, flexShrink: 0, display: "flex", flexDirection: "column", gap: 2 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: T.fsXs, color: T.mid }}>Progress</span>
          {percentOverride !== null && percentOverride !== undefined && <span style={{ fontSize: T.fsXxs, color: T.accent }}>(Override)</span>}
        </div>
        <OkrProgressBar percent={progressPercent} size="sm" color={T.accent} />
      </div>

      <div style={{ width: 70, flexShrink: 0, display: "flex", justifyContent: "flex-end", gap: 4 }}>
        <button
          type="button"
          onClick={() => onUpdates(initiative)}
          style={{
            background: "transparent", border: "none", cursor: "pointer",
            color: T.blue, padding: 4, borderRadius: 4,
            opacity: 1, transition: "opacity 0.2s ease"
          }}
          title="Updates"
        >
          <MessageSquare size={14} />
        </button>
        {canEdit && (
          <button
            type="button"
            onClick={() => onEdit(initiative)}
            style={{
              background: "transparent", border: "none", cursor: "pointer",
              color: T.blue, padding: 4, borderRadius: 4,
              opacity: hover ? 1 : 0, transition: "opacity 0.2s ease"
            }}
            title="Edit Initiative"
          >
            <Edit2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}
